/**
 * ToolFinderPage.jsx
 *
 * Hosts the Tool Finder wizard. The wizard's first step IS the hub — no
 * separate marketing page (per Group D plan, Q1=1d).
 *
 * Routes served:
 *   - /toolfinder           → wizard from current step (ENTRY by default)
 *   - /toolfinder/results   → wizard switches to RESULTS internally
 *
 * Provider chain (top → bottom):
 *   <WizardProvider> — wizard state, selections, navigation handlers.
 *   <ProductProvider> (Sub-Plan 3 SP3.A.1) — reads selections via useWizard,
 *     loads the ToolFinder bundle, derives effective threads, exposes
 *     filter + tab state for the Results page.
 *
 * @module pages/ToolFinderPage
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D1)
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WizardProvider, useWizard } from '@contexts/WizardContext';
import { ProductProvider } from '@contexts/ProductContext';
import WizardLayout from '@components/wizard/WizardLayout';
import StepEntryPoint from '@components/wizard/StepEntryPoint';
import StepApplication from '@components/wizard/StepApplication';
import StepDrillingMethod from '@components/wizard/StepDrillingMethod';
import StepEquipmentClass from '@components/wizard/StepEquipmentClass';
import StepEquipmentThreadChoice from '@components/wizard/StepEquipmentThreadChoice';
import StepHoleDiameter from '@components/wizard/StepHoleDiameter';
import StepRockConditions from '@components/wizard/StepRockConditions';
import StepSummary from '@components/wizard/StepSummary';
// Replace-path components (SP3.A.3).
import StepEquipmentSelect from '@components/wizard/StepEquipmentSelect';
import StepThreadChoice from '@components/wizard/StepThreadChoice';
// Results page (SP3.B.2 — D9).
import ResultsPage from '@pages/ResultsPage';
import { STEPS } from '@components/wizard/wizardConstants';
import ContactHelpBanner from '@components/contact/ContactHelpBanner';

function StepStub({ stepName }) {
  return (
    <div className="text-center text-n6 max-w-xl mx-auto py-12">
      <p className="font-sandvik-headline text-lg font-normal text-carbon-black">
        Step &quot;{stepName}&quot;
      </p>
      <p className="mt-2 font-sandvik-text text-sm">
        Component for this step is built in a later Sub-Plan task.
      </p>
    </div>
  );
}

function WizardSwitch() {
  const { currentStep } = useWizard();
  switch (currentStep) {
    case STEPS.ENTRY:
      return <StepEntryPoint />;
    case STEPS.APPLICATION:
      return <StepApplication />;
    case STEPS.DRILLING_METHOD:
      return <StepDrillingMethod />;
    case STEPS.EQUIPMENT_CLASS:
      return <StepEquipmentClass />;
    case STEPS.EQUIPMENT_THREAD_CHOICE:
      return <StepEquipmentThreadChoice />;
    case STEPS.HOLE_DIAMETER:
      return <StepHoleDiameter />;
    case STEPS.ROCK_CONDITIONS:
      return <StepRockConditions />;
    case STEPS.EQUIPMENT_SELECT:
      return <StepEquipmentSelect />;
    case STEPS.THREAD_CHOICE:
      return <StepThreadChoice />;
    case STEPS.SUMMARY:
      return <StepSummary />;
    case STEPS.RESULTS:
      return <ResultsPage />;
    default:
      return <StepStub stepName={currentStep} />;
  }
}

/**
 * RoutingBridge — sits inside WizardProvider so it can subscribe to the
 * wizard's onReachResults callback and navigate the router. Same pattern
 * the legacy ToolFinderWizardPage used. No UI of its own.
 */
function RoutingBridge({ onReachResults }) {
  const { currentStep } = useWizard();
  useEffect(() => {
    if (currentStep === STEPS.RESULTS && typeof onReachResults === 'function') {
      onReachResults();
    }
  }, [currentStep, onReachResults]);
  return null;
}

export default function ToolFinderPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleReachResults = () => {
    if (!location.pathname.endsWith('/results')) {
      navigate('/toolfinder/results', { replace: false });
    }
  };

  return (
    <WizardProvider>
      <ProductProvider>
        <RoutingBridge onReachResults={handleReachResults} />
        <WizardLayout>
          <WizardSwitch />
          <ToolFinderHelpBanner />
        </WizardLayout>
      </ProductProvider>
    </WizardProvider>
  );
}

function ToolFinderHelpBanner() {
  return (
    <ContactHelpBanner
      title="Can't find what you're looking for?"
      topics={[
        { topicId: 'missing-rig', label: "Can't find rig/model", icon: 'construction' },
        { topicId: 'missing-drill', label: "Can't find rock drill", icon: 'handyman' },
        { topicId: 'product-info', label: 'Product information', icon: 'info' },
      ]}
    />
  );
}
